import { useState } from 'react';

export type PaperWidth = '58mm' | '80mm' | '110mm';
export type PrintFontFamily =
  | 'monospace'
  | "'Courier New', monospace"
  | "'Tahoma', sans-serif"
  | 'Arial, sans-serif'
  | 'Verdana, sans-serif';

export interface PrintConfig {
  paperWidth: PaperWidth;
  fontSize: number; // base px, rango 7–14
  fontFamily: PrintFontFamily;
  printScale: number; // % de escala física al imprimir (calibración de impresora), 50–200
}

const STORAGE_KEY = 'printConfig:v1';

export const DEFAULT_PRINT_CONFIG: PrintConfig = {
  paperWidth: '58mm',
  fontSize: 10,
  fontFamily: 'monospace',
  printScale: 100,
};

export const PRINT_SCALE_OPTIONS = [50, 75, 100, 125, 150, 175, 200, 210, 220, 230, 240, 250] as const;

// Escala de tamaños derivada del tamaño base elegido por el usuario
export const getFontScale = (base: number) => ({
  xs:    Math.max(6, base - 2),
  sm:    Math.max(7, base - 1),
  base,
  md:    base + 1,
  title: base + 3,
});

// El contenido se renderiza a paperWidth*printScale y la impresora lo
// comprime físicamente al ancho real del rollo, así que tanto el tamaño de
// letra como cualquier padding/margen en px deben escalarse igual para
// mantener las mismas proporciones que la vista web.
export const getPrintScaleRatio = (config: PrintConfig): number => (config.printScale || 100) / 100;

export const getScaledFontScale = (config: PrintConfig) =>
  getFontScale(config.fontSize * getPrintScaleRatio(config));

export const printPx = (config: PrintConfig, n: number): number => n * getPrintScaleRatio(config);

// Imprime el contenido HTML del ticket: directo por Electron (sin diálogo) o,
// en la web, vía sessionStorage + ventana /print con window.print().
export const printTicketHtml = async (contenido: string, config: PrintConfig): Promise<void> => {
  const electronAPI = (window as any).electronAPI;

  if (electronAPI?.printContent) {
    const ratio = getPrintScaleRatio(config);
    const paperWidthMm = parseInt(config.paperWidth, 10);
    const contentWidthMm = paperWidthMm * ratio;
    const px = (n: number) => printPx(config, n);
    const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: ${config.fontFamily}; width: ${contentWidthMm}mm; max-width: ${contentWidthMm}mm; padding: ${px(4)}px; margin: 0; line-height: 1.4; }
    table { table-layout: fixed; width: 100%; }
    @media print { @page { margin: 0; size: ${contentWidthMm}mm auto; } body { padding: ${px(2)}px; } }
  </style>
</head>
<body>${contenido}<div style="height:${px(24)}px"></div></body>
</html>`;
    try {
      const result = await electronAPI.printContent(fullHtml, paperWidthMm, config.printScale);
      console.warn('[Print] Impreso OK:', result);
    } catch (err) {
      console.error('[Print] Error al imprimir:', err);
    }
    return;
  }

  // Web: guardar en sessionStorage y abrir ventana /print
  try {
    sessionStorage.setItem('printContent', contenido);
    sessionStorage.setItem('printConfigOverride', JSON.stringify(config));
  } catch { /* ignorar modo privado */ }
  window.open('/print', '_blank', 'width=700,height=900,menubar=no,toolbar=no,status=no');
};

// Ancho en px de pantalla (96 dpi) para la vista previa: ancho completo del papel
export const PAPER_WIDTH_PX: Record<PaperWidth, number> = {
  '58mm':  220,  // 58mm * 96/25.4
  '80mm':  303,  // 80mm * 96/25.4
  '110mm': 416,  // 110mm * 96/25.4
};

export const PAPER_WIDTH_INFO: Record<PaperWidth, { area: string; desc: string }> = {
  '58mm':  { area: '48mm',  desc: 'Vouchers, máquinas POS móviles, comercios pequeños.' },
  '80mm':  { area: '72mm',  desc: 'Estándar SII, supermercados, retail.' },
  '110mm': { area: '104mm', desc: 'Etiquetas de despacho o logística.' },
};

export const FONT_SIZE_OPTIONS = [7, 8, 9, 10, 11, 12, 13, 14, 16] as const;

export const FONT_FAMILY_OPTIONS: { value: PrintFontFamily; label: string }[] = [
  { value: 'monospace',                   label: 'Monoespaciada' },
  { value: "'Courier New', monospace",    label: 'Courier New'   },
  { value: "'Tahoma', sans-serif",         label: 'Tahoma'        },
  { value: 'Arial, sans-serif',           label: 'Arial'         },
  { value: 'Verdana, sans-serif',         label: 'Verdana'       },
];

export const readPrintConfig = (): PrintConfig => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PRINT_CONFIG;
    return { ...DEFAULT_PRINT_CONFIG, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PRINT_CONFIG;
  }
};

export const savePrintConfig = (config: PrintConfig): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch { /* ignorar en modo privado */ }
};

export const usePrintConfig = () => {
  const [saved, setSaved] = useState<PrintConfig>(readPrintConfig);
  const [draft, setDraft] = useState<PrintConfig>(readPrintConfig);

  const updateDraft = (partial: Partial<PrintConfig>) => {
    setDraft(prev => ({ ...prev, ...partial }));
  };

  const save = () => {
    setSaved(draft);
    savePrintConfig(draft);
  };

  const discard = () => {
    setDraft(saved);
  };

  const reset = () => {
    setDraft(DEFAULT_PRINT_CONFIG);
  };

  const isDirty = JSON.stringify(saved) !== JSON.stringify(draft);

  return { config: saved, draft, updateDraft, save, discard, reset, isDirty };
};
